import { useCallback } from 'react';
import { updateReadOnlyFieldStatus } from '~app/Pages/Setup/RestrictedFields/helpers/restrictedFieldsHelpers.js';

/**
 * useRestrictedFields
 *
 * Owns the two restricted-field methods that were previously class methods
 * on FormViewer, called back from useSocketMessage via socketCallbacksRef:
 *
 *   updateRestrictedFields(data) — applies read-only status to fields and
 *                                  stores restricted-field data in state
 *   removeChoiceMenuButton()     — strips the "Choices" button from the
 *                                  field-event menu if the field is restricted
 *
 * Returns:
 *   { updateRestrictedFields, removeChoiceMenuButton }
 */
const useRestrictedFields = ({
  currentElRef, // shared ref to the currently focused field element
  fieldEventsRef, // kept-current ref to useFormFieldEvents result
  miscStateRef, // kept-current ref to useMiscState result
}) => {
  /**
   * Strips the "Choices" button from the field button menu when the currently
   * focused field is marked as restricted.
   */
  const removeChoiceMenuButton = useCallback(() => {
    const element = currentElRef?.current;
    if (element?.classList?.contains('restricted')) {
      const fieldEvents = fieldEventsRef.current;
      const filtered = fieldEvents.menuButtonsChoices.filter(item => item?.name !== 'Choices');
      fieldEvents.setMenuButtonsChoices(filtered);
    }
  }, [currentElRef, fieldEventsRef]);

  /**
   * Applies read-only DOM styling to the restricted fields received from the
   * server, stores the data for render, then trims the choice button if needed.
   *
   * @param {{ fields: Array }} data
   */
  const updateRestrictedFields = useCallback(
    data => {
      updateReadOnlyFieldStatus(data.fields);
      miscStateRef.current.setRestrictedFieldData(data);
      removeChoiceMenuButton();
    },
    [miscStateRef, removeChoiceMenuButton],
  );

  return {
    updateRestrictedFields,
    removeChoiceMenuButton,
  };
};

export default useRestrictedFields;
